import { Model } from '@vuex-orm/core';
import VehicleAPI from '@/api/VehicleAPI';
import { VehicleCreatePayload, VehicleUpdatePayload } from '@/interfaces/IVehicle';

/**
 * Vehicle model for logistics
 * Represents a vehicle with registration info
 */
export default class Vehicle extends Model {
    public id!: string;
    public registration!: string;
    public type!: string;
    public purpose!: string;
    public driver!: string;
    public createdAt!: string;
    public updatedAt!: string;

    public static entity = 'vehicles';

    public static fields() {
        return {
            id: this.attr(null),
            registration: this.attr(null),
            type: this.attr(null),
            purpose: this.attr(null),
            driver: this.attr(null),
            createdAt: this.attr(null),
            updatedAt: this.attr(null),
        };
    }

    /**
     * Fetch all vehicles from the API
     */
    public static async fetchAll(): Promise<Vehicle[]> {
        try {
            const response = await VehicleAPI.getAll();
            const vehicles = response?.data || [];
            await Vehicle.create({ data: vehicles });
            return vehicles;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Fetch a single vehicle by ID
     */
    public static async fetchById(id: string): Promise<Vehicle | null> {
        try {
            const response = await VehicleAPI.getById(id);
            const vehicle = response?.data;
            if (vehicle) {
                await Vehicle.insertOrUpdate({ data: vehicle });
            }
            return vehicle;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Create a new vehicle
     */
    public static async createNew(payload: VehicleCreatePayload): Promise<Vehicle> {
        try {
            const response = await VehicleAPI.create(payload);
            const vehicle = response?.data;
            if (vehicle) {
                await Vehicle.insertOrUpdate({ data: vehicle });
            }
            return vehicle;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Update an existing vehicle
     */
    public static async updateExisting(id: string, payload: VehicleUpdatePayload): Promise<Vehicle> {
        try {
            const response = await VehicleAPI.update(id, payload);
            const vehicle = response?.data;
            if (vehicle) {
                await Vehicle.insertOrUpdate({ data: vehicle });
            }
            return vehicle;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Delete a vehicle
     */
    public static async deleteById(id: string): Promise<void> {
        try {
            await VehicleAPI.delete(id);
            await Vehicle.delete(id);
        } catch (e) {
            return Promise.reject(e);
        }
    }
}
